import {allCompaniesPolicy} from '../../../models/company-sync-eligibility.model';
import {CompanySyncRulesComponent} from './company-sync-rules/company-sync-rules.component';
import {DatePipe} from '@angular/common';
import {Component, inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {
  EnrichSettings,
  HubspotProperty,
  HubspotAdditionalProperties,
  HubspotSubUnitPreviewResponse,
  HubspotSubUnitPreviewRow,
} from '../../../models/hubspot.model';
import {RegistryProvider, RegistryProviderStatus, RegistryStatusResponse} from '../../../models/registry.model';
import {MaterialModule} from '../../../material.module';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {ClientService} from '../../../services/auth/client.service';
import {
  ConnectConnectionsService,
  type ConnectServerConnection,
} from '../../../services/connect/connect-connections.service';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {HubspotAuthService} from '../../../services/enrich/hubspot-auth.service';
import {ReferenceService} from '../../../services/enrich/reference.service';
import {RegistryService} from '../../../services/enrich/registry.service';
import {SidebarService} from '../../../services/layout/sidebar.service';
import {ToastService} from '../../../services/layout/toast.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {ApiKeysComponent} from '../../client-settings/api-keys/api-keys.component';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';
import {DnbUsageCardComponent} from './dnb-usage-card/dnb-usage-card.component';
import {AdditionalCompanyPropertiesComponent} from './additional-company-properties/additional-company-properties.component';

type RegistryProviderOption = {
  id: RegistryProvider;
  label: string;
  country: string;
  summary: string;
  refreshSummary: string;
};

type RegistryDataSource = 'enin' | 'dnb';
type DnbCountryISOAlpha2Code = 'NO' | 'SE' | 'ES' | 'PL' | 'AE';

type DnbConnectionState = 'loading' | 'ready' | 'not_configured' | 'ambiguous' | 'unavailable';

type RegistryDataSourceOption = {
  id: RegistryDataSource;
  label: string;
  summary: string;
  propertySummary: string;
};

type PropertyOverwriteOption = {
    propertyName: string;
    label: string;
    source: string;
};

const REGISTRY_PROVIDER_ORDER: RegistryProvider[] = ['brreg', 'scb', 'cvr'];
const DEFAULT_REGISTRY_PROVIDERS: RegistryProvider[] = [...REGISTRY_PROVIDER_ORDER];
const REGISTRY_DATA_SOURCE_ORDER: RegistryDataSource[] = ['enin', 'dnb'];
const DNB_PROVIDER_REF = 'dnb.direct_plus';
const DNB_COUNTRY_OPTIONS: ReadonlyArray<{value: DnbCountryISOAlpha2Code; label: string}> = [
  {value: 'NO', label: 'Norway'},
  {value: 'SE', label: 'Sweden'},
  {value: 'ES', label: 'Spain'},
  {value: 'PL', label: 'Poland'},
  {value: 'AE', label: 'United Arab Emirates'},
];
const DNB_SUBJECT_REF_PATTERN = /^ep-subject-v1\.[A-Za-z0-9_-]{1,64}\.[A-Za-z0-9_-]{43}$/;
const ALWAYS_OVERWRITE_PROPERTIES = new Set([
    'shine_enrich_synced',
    'shine_enrich_first_synced_at',
    'shine_enrich_last_synced_at',
    'shine_enrich_company_status',
]);
const STANDARD_PROPERTY_OVERWRITE_OPTIONS: PropertyOverwriteOption[] = [
    {propertyName: 'name', label: 'Company name', source: 'company_name'},
    {propertyName: 'address', label: 'Street address', source: 'street_address / postal_street_address'},
    {propertyName: 'city', label: 'City', source: 'city / postal_place'},
    {propertyName: 'country', label: 'Country', source: 'country_select'},
    {propertyName: 'zip', label: 'Postal code', source: 'business_address_postal_code / postal_code'},
    {propertyName: 'phone', label: 'Phone', source: 'phone_number / mobile_phone_number'},
    {propertyName: 'annualrevenue', label: 'Annual revenue', source: 'latest_annual_revenue'},
    {propertyName: 'founded_year', label: 'Founded year', source: 'date_founded'},
];
const REGISTRY_PROVIDER_OPTIONS: RegistryProviderOption[] = [
  {
    id: 'brreg',
    label: 'Brønnøysundregistrene (BRREG)',
    country: 'Norway',
    summary: 'Norwegian companies, sub-units, and BRREG announcements.',
    refreshSummary: 'Supports local cache refresh and the announcements timeline sync.',
  },
  {
    id: 'scb',
    label: 'SCB / Bolagsverket',
    country: 'Sweden',
    summary: 'Swedish company baseline from SCB and high-value registry datasets.',
    refreshSummary: 'Works through the Nordic registry cache and local refresh pipeline.',
  },
  {
    id: 'cvr',
    label: 'CVR / Datafordeler',
    country: 'Denmark',
    summary: 'Danish legal entities from CVR with local cache refresh support.',
    refreshSummary: 'Best fit for event-driven refresh when Datafordeler access is configured.',
  },
];

const REGISTRY_DATA_SOURCE_OPTIONS: RegistryDataSourceOption[] = [
  {
    id: 'enin',
    label: 'Enin',
    summary: 'Use shared Enin data for entitled clients across Norway, Sweden, and Denmark.',
    propertySummary: 'Creates Enin-specific HubSpot properties only for clients with this source enabled.',
  },
  {
    id: 'dnb',
    label: 'Dun & Bradstreet Direct+',
    summary: 'Resolve companies and their global ultimate group across countries and HubSpot portals.',
    propertySummary: 'Paid tenant-owned source. Uses bounded on-demand reads and does not enable watchlist enrollment.',
  },
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isReadyDnbConnection = (connection: ConnectServerConnection, clientId: string): boolean => {
  const record = connection as ConnectServerConnection & {
    clientId?: unknown;
    client_id?: unknown;
    tenant_id?: unknown;
    documentType?: unknown;
    connection_ref?: unknown;
    metadata?: unknown;
  };
  const id = record.id;
  if (
    record.clientId !== clientId ||
    record.client_id !== clientId ||
    record.tenant_id !== clientId ||
    record.documentType !== 'connection' ||
    typeof id !== 'string' ||
    !id ||
    id.length > 200 ||
    id !== id.trim().normalize('NFC').toLowerCase() ||
    record.ref !== DNB_PROVIDER_REF ||
    record.provider_ref !== DNB_PROVIDER_REF ||
    (record.connection_ref !== undefined && record.connection_ref !== DNB_PROVIDER_REF) ||
    record.config !== null ||
    !isRecord(record.metadata)
  ) {
    return false;
  }

  const provider = record.metadata['enrichment_provider'];
  if (!isRecord(provider)) {
    return false;
  }

  const subjectRef = provider['subject_ref'];
  return (
    Object.keys(provider).sort().join(',') ===
      'credential_mode,enabled,provider_ref,schema_version,subject_ref' &&
    provider['schema_version'] === 'enrichment-provider-connection-view-v1' &&
    provider['provider_ref'] === DNB_PROVIDER_REF &&
    provider['credential_mode'] === 'tenant_owned' &&
    provider['enabled'] === true &&
    typeof subjectRef === 'string' &&
    DNB_SUBJECT_REF_PATTERN.test(subjectRef)
  );
};

const isDnbConnectionCandidate = (connection: ConnectServerConnection): boolean => {
  const record = connection as ConnectServerConnection & {
    connection_ref?: unknown;
    metadata?: unknown;
  };
  const provider = isRecord(record.metadata) ? record.metadata['enrichment_provider'] : null;
  return (
    record.ref === DNB_PROVIDER_REF ||
    record.provider_ref === DNB_PROVIDER_REF ||
    record.connection_ref === DNB_PROVIDER_REF ||
    (isRecord(provider) && provider['provider_ref'] === DNB_PROVIDER_REF)
  );
};

const evaluateDnbConnectionState = (
  connections: ConnectServerConnection[] | null | undefined,
  clientId: string,
): DnbConnectionState => {
  const dnbConnections = (connections ?? []).filter(isDnbConnectionCandidate);
  if (dnbConnections.length > 1) {
    return 'ambiguous';
  }
  return dnbConnections.length === 1 && isReadyDnbConnection(dnbConnections[0], clientId)
    ? 'ready'
    : 'not_configured';
};

const sortRegistryProviders = (providers: RegistryProvider[]): RegistryProvider[] =>
  REGISTRY_PROVIDER_ORDER.filter((provider) => providers.includes(provider));

const normalizeSettingsProviders = (settings?: Partial<EnrichSettings> | null): RegistryProvider[] => {
  const dataProviders = Array.isArray(settings?.dataProviders)
    ? settings.dataProviders.filter((provider): provider is RegistryProvider => REGISTRY_PROVIDER_ORDER.includes(provider))
    : [];

  if (dataProviders.length > 0) {
    return sortRegistryProviders(Array.from(new Set(dataProviders)));
  }

  const legacyProvider = settings?.dataProvider;
  return legacyProvider && REGISTRY_PROVIDER_ORDER.includes(legacyProvider) ? [legacyProvider] : [...DEFAULT_REGISTRY_PROVIDERS];
};

const normalizeSettingsDataSources = (settings?: Partial<EnrichSettings> | null): RegistryDataSource[] => {
  const dataSources = Array.isArray(settings?.dataSources)
    ? settings.dataSources.filter((source): source is RegistryDataSource =>
        REGISTRY_DATA_SOURCE_ORDER.includes(source as RegistryDataSource),
      )
    : [];

  return REGISTRY_DATA_SOURCE_ORDER.filter((source) => dataSources.includes(source));
};

@Component({
  selector: 'app-enrich-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  imports: [MaterialModule, ReactiveFormsModule, BreadcrumbsComponent, RouterLink, DatePipe, DnbUsageCardComponent, CompanySyncRulesComponent, AdditionalCompanyPropertiesComponent]
})
export class EnrichSettingsComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private hubspotService = inject(HubspotService);
  private hubspotAuthService = inject(HubspotAuthService);
  private registryService = inject(RegistryService);
  private clientService = inject(ClientService);
  private clientLoadingService = inject(ClientLoadingService);
  private connectConnectionsService = inject(ConnectConnectionsService);
  private referenceService = inject(ReferenceService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private sidebarService = inject(SidebarService);
  private renderer = inject(Renderer2);
  private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);

  get selectedClientId(): string | undefined {return this.clientService.client?.id;}

  portalId: number | null = null;
  hubspotConnectionId?: string;
  hubspotPropertiesReady = false;
  private settingsClientId?: string;
  private settingsLoadVersion = 0;
  private propertiesLoadVersion = 0;
  companyCaptureScriptSnippet = '';
  companyCaptureInitSnippet = '';
  companyCaptureWorkflowSnippet = '';
  companyCaptureAccess: 'checking' | 'granted' | 'missing' | 'unavailable' = 'missing';
  companyCaptureAccessMessage = '';
  isAuthorizingCompanyCapture = false;
  companyCapturePermissionConnectionId?: string;
  private companyCaptureAccessVersion = 0;
  private companyCaptureAuthorization?: AbortController;
  private destroyed = false;

  settingsForm: FormGroup;
  isLoading = true;
  isSaving = false;
  isSettingUpProperties = false;
  isSyncing = false;
  isLoadingRegistryStatus = false;
  error: string | null = null;
  propertiesError: string | null = null;
  registryStatusError: string | null = null;
  dnbConnectionState: DnbConnectionState = 'loading';
  isLoadingSubUnitPreview = false;
  subUnitPreviewError: string | null = null;
  subUnitPreview: HubspotSubUnitPreviewResponse | null = null;
  selectedIndex = 0;
  queryParamName = 'tab';
  isSetupMode = false;
  readonly subUnitPreviewPageSize = 25;
  private hasLoadedSubUnitPreview = false;
  private dnbConnectionRequestVersion = 0;
  private settingsSaveAttemptVersion = 0;

  readonly syncFrequencyOptions = [
    {value: 'daily', label: 'Daily'},
    {value: 'weekly', label: 'Weekly'},
    {value: 'monthly', label: 'Monthly'},
    {value: 'off', label: 'Off'}
  ];
  hubspotProperties: HubspotProperty[] = [];
    propertyOverwriteOptions: PropertyOverwriteOption[] = [];
  readonly registryProviderOptions = REGISTRY_PROVIDER_OPTIONS;
  readonly registryDataSourceOptions = REGISTRY_DATA_SOURCE_OPTIONS;
  registryStatus: RegistryStatusResponse | null = null;
  registryStatuses: RegistryProviderStatus[] = [];

  readonly breadcrumbs = [
    {label: 'Shine Enrich', route: '/enrich/setup'},
    {label: 'Settings'}
  ];
  readonly companyCaptureScriptCopyMessage = 'Company capture script copied to clipboard';
  readonly companyCaptureInitCopyMessage = 'Initialization snippet copied to clipboard';
  readonly companyCaptureWorkflowCopyMessage = 'Workflow request copied to clipboard';
  readonly syncAllImpactText =
    'Starts a full sync for every HubSpot company for this client. Existing properties may be updated according to the saved overwrite policy.';
  readonly subUnitPreviewImpactText =
    'Preview one bounded page of BRREG-backed sub-unit changes without writing to HubSpot or starting a sync job.';
  protected readonly environment = environment;
  readonly dnbCountryOptions = DNB_COUNTRY_OPTIONS;

  constructor() {
    this.settingsForm = this.fb.group({
      companySyncEligibility: [allCompaniesPolicy()],
      subscriptionActive: [false],
      syncOnCompanyCreated: [false],
      existingCompanySyncFrequency: ['off', Validators.required],
      addCompaniesMatchingFilters: [false],
      companyCaptureEnabled: [false],
      dataProviders: [[...DEFAULT_REGISTRY_PROVIDERS], Validators.required],
      dataSources: [[]],
      dnbCountryISOAlpha2Code: [null as DnbCountryISOAlpha2Code | null],
      announcementsTimelineSyncEnabled: [false],
      crmOrgNumberProperty: ['', Validators.required],
      crmSubUnitNumberProperty: [''],
      populateParentOrganizationNumberOnSubUnits: [true],
      propertyOverwriteToggles: this.fb.group({}),
      hubspotAdditionalProperties: new FormControl<HubspotAdditionalProperties | null>(null)
    });

    this.settingsForm.get('crmSubUnitNumberProperty')?.valueChanges.subscribe(() => {
      this.resetSubUnitPreviewState();
    });
  }

  ngOnInit(): void {
    const indexParam = this.route.snapshot.queryParamMap.get(this.queryParamName);
    const parsedIndex = parseInt(indexParam || '', 10);
    this.selectedIndex = !Number.isNaN(parsedIndex) && parsedIndex >= 0 ? parsedIndex : 0;
    this.isSetupMode = this.route.snapshot.queryParamMap.get('setup') === 'true';

    this.route.queryParams.subscribe((params) => {
      const paramValue = parseInt(params[this.queryParamName] || '', 10);
      this.selectedIndex = !Number.isNaN(paramValue) && paramValue >= 0 ? paramValue : 0;
      this.isSetupMode = params['setup'] === 'true';

      if (this.selectedIndex === 2 && !this.isLoading) {
        this.ensureSubUnitPreviewLoaded();
      }
    });

    this.isLoading = true;
    this.error = null;

    this.clientLoadingService.ensureClientSelected().subscribe({
      next: () => {
        this.loadSettings();
      },
      error: (err) => {
        this.enrichDiagnostics.reportError('enrich.settings.client-selection', err);
        this.error = 'No client selected. Please select a client from the dropdown.';
        this.isLoading = false;
      }
    });
  }

  get selectedRegistryProviders(): RegistryProvider[] {
    const value = this.settingsForm.get('dataProviders')?.value;
    return sortRegistryProviders(Array.isArray(value) ? value as RegistryProvider[] : []);
  }

  get selectedRegistryDataSources(): RegistryDataSource[] {
    const value = this.settingsForm.get('dataSources')?.value;
    return normalizeSettingsDataSources({dataSources: Array.isArray(value) ? value : []});
  }

    get propertyOverwriteToggleGroup(): FormGroup {
        return this.settingsForm.get('propertyOverwriteToggles') as FormGroup;
    }

  get canPreviewSubUnits(): boolean {
    return this.isProviderSelected('brreg') && !!this.subUnitPreviewPropertyName;
  }

  get subUnitPreviewPropertyName(): string {
    return String(this.settingsForm.get('crmSubUnitNumberProperty')?.value || '').trim();
  }

  get subUnitPreviewRows(): HubspotSubUnitPreviewRow[] {
    return this.subUnitPreview?.rows ?? [];
  }

  get subUnitPreviewSummary() {
    return this.subUnitPreview?.summary ?? null;
  }

  get canLoadNextSubUnitPreviewPage(): boolean {
    return !!this.subUnitPreview?.nextAfter && this.subUnitPreview?.hasMore === true && !this.isLoadingSubUnitPreview;
  }

  get hasBackendSubUnitPreviewConfigEmpty(): boolean {
    return this.subUnitPreview?.configurationState === 'config_empty';
  }

  get subUnitPreviewConfigMessage(): string {
    if (this.hasBackendSubUnitPreviewConfigEmpty) {
      const issues = this.subUnitPreview?.configurationIssues ?? [];
      if (issues.includes('brreg_disabled')) {
        return 'Enable BRREG under Registry providers before loading the sub-unit preview.';
      }
      if (issues.includes('crm_subunit_property_missing')) {
        return 'Select the HubSpot sub-unit number property in Property mapping before loading the preview.';
      }
      return 'Complete the BRREG sub-unit preview configuration before loading preview rows.';
    }

    if (!this.isProviderSelected('brreg')) {
      return 'Enable BRREG under Registry providers before loading the sub-unit preview.';
    }

    if (!this.subUnitPreviewPropertyName) {
      return 'Select the HubSpot sub-unit number property in Property mapping before loading the preview.';
    }

    return '';
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedIndex = event.index;
    if (event.index === 2) {
      this.ensureSubUnitPreviewLoaded();
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[this.queryParamName]: event.index},
      queryParamsHandling: 'merge'
    });
  }

  isProviderSelected(provider: RegistryProvider): boolean {
    return this.selectedRegistryProviders.includes(provider);
  }

  isDataSourceSelected(source: RegistryDataSource): boolean {
    return this.selectedRegistryDataSources.includes(source);
  }

  isDataSourceAvailable(source: RegistryDataSource): boolean {
    return source !== 'dnb' || this.dnbConnectionState === 'ready';
  }

  isDataSourceSelectionDisabled(source: RegistryDataSource): boolean {
    return !this.isDataSourceAvailable(source) && !this.isDataSourceSelected(source);
  }

  get dnbConnectionStatusMessage(): string {
    switch (this.dnbConnectionState) {
      case 'ready':
        return 'Tenant-owned D&B Connection is ready. This paid source can be enabled.';
      case 'ambiguous':
        return 'Multiple D&B Connections were found. Keep one reconciled tenant-owned Connection before enabling.';
      case 'unavailable':
        return 'D&B Connection status is unavailable. The source remains disabled.';
      case 'loading':
        return 'Checking the tenant-owned D&B Connection…';
      default:
        return 'Set up and reconcile a tenant-owned D&B Direct+ Connection before enabling.';
    }
  }

  isAnnouncementsSupported(): boolean {
    return this.isProviderSelected('brreg');
  }

  getRegistryProviderStatus(provider: RegistryProvider): RegistryProviderStatus | undefined {
    return this.registryStatuses.find((status) => status.provider === provider);
  }

  onProviderSelectionChange(provider: RegistryProvider, checked: boolean): void {
    const nextProviders = checked
      ? sortRegistryProviders(Array.from(new Set([...this.selectedRegistryProviders, provider])))
      : this.selectedRegistryProviders.filter((item) => item !== provider);

    this.settingsForm.patchValue({dataProviders: nextProviders});
    this.syncAnnouncementsToggleWithProviders(nextProviders);
    this.resetSubUnitPreviewState();
  }

  onDataSourceSelectionChange(source: RegistryDataSource, checked: boolean): void {
    if (checked && !this.isDataSourceAvailable(source)) {
      return;
    }

    const nextSources = checked
      ? normalizeSettingsDataSources({dataSources: [...this.selectedRegistryDataSources, source]})
      : this.selectedRegistryDataSources.filter((item) => item !== source);

    this.settingsForm.patchValue({dataSources: nextSources});
  }

  loadSettings(): void {
    this.companyCaptureAuthorization?.abort();
    this.companyCaptureAccessVersion++;
    this.companyCapturePermissionConnectionId = undefined;
    this.companyCaptureAccess = 'missing';
    this.isLoading = true;
    this.error = null;
    const clientId = this.clientService.client?.id;
    const requestVersion = ++this.settingsLoadVersion;

    if (!clientId) {
      this.error = 'No client selected';
      this.isLoading = false;
      return;
    }

    this.hubspotService.getSettings(clientId).subscribe(
      (settings: EnrichSettings) => {
        if (requestVersion !== this.settingsLoadVersion || this.clientService.client?.id !== clientId) return;
        this.settingsClientId = clientId;
        const enabledProviders = normalizeSettingsProviders(settings);
        const enabledDataSources = normalizeSettingsDataSources(settings);
        this.portalId = settings.portalId;
        this.hubspotConnectionId = settings.hubspotConnectionId;
        this.buildCompanyCaptureSnippets(settings.portalId);
        this.settingsForm.patchValue({
          companySyncEligibility: settings.companySyncEligibility ?? allCompaniesPolicy(),
          subscriptionActive: settings.subscriptionActive,
          syncOnCompanyCreated: settings.syncOnCompanyCreated,
          existingCompanySyncFrequency: settings.existingCompanySyncFrequency,
          addCompaniesMatchingFilters: settings.addCompaniesMatchingFilters,
          companyCaptureEnabled: settings.companyCaptureEnabled ?? false,
          dataProviders: enabledProviders,
          dataSources: enabledDataSources,
          dnbCountryISOAlpha2Code: settings.dnbCountryISOAlpha2Code ?? null,
          announcementsTimelineSyncEnabled: settings.announcementsTimelineSyncEnabled ?? false,
          crmOrgNumberProperty: settings.crmOrgNumberProperty,
          crmSubUnitNumberProperty: settings.crmSubUnitNumberProperty,
          populateParentOrganizationNumberOnSubUnits:
            settings.populateParentOrganizationNumberOnSubUnits !== false,
          hubspotAdditionalProperties: settings.hubspotAdditionalProperties ?? null,
        });
        this.syncPropertyOverwriteControls(settings);
        this.syncAnnouncementsToggleWithProviders(enabledProviders);
        if (settings.companyCaptureEnabled === true) void this.refreshCompanyCaptureAccess(clientId);
        this.isLoading = false;
        if (this.selectedIndex === 2) {
          this.ensureSubUnitPreviewLoaded();
        }
      },
      () => {
        if (requestVersion !== this.settingsLoadVersion || this.clientService.client?.id !== clientId) return;
        this.error = 'Failed to load settings';
        this.isLoading = false;
      }
    );

    this.loadHubspotProperties(clientId);
    this.loadRegistryStatus(clientId);
    this.loadDnbConnectionStatus(clientId);
  }

  loadDnbConnectionStatus(clientId = this.clientService.client?.id): void {
    this.refreshDnbConnectionStatus(clientId);
  }

  private refreshDnbConnectionStatus(
    clientId: string | undefined,
    onComplete?: (state: DnbConnectionState) => void,
  ): void {
    const requestVersion = ++this.dnbConnectionRequestVersion;
    this.dnbConnectionState = 'loading';
    if (!clientId || this.clientService.client?.id !== clientId) {
      this.dnbConnectionState = 'unavailable';
      onComplete?.('unavailable');
      return;
    }

    const complete = (state: DnbConnectionState): void => {
      const isCurrentRequest =
        requestVersion === this.dnbConnectionRequestVersion &&
        this.clientService.client?.id === clientId;
      if (isCurrentRequest) {
        this.dnbConnectionState = state;
      }
      onComplete?.(isCurrentRequest ? state : 'unavailable');
    };

    this.connectConnectionsService.list().subscribe({
      next: (connections) => {
        complete(evaluateDnbConnectionState(connections, clientId));
      },
      error: () => {
        complete('unavailable');
      },
    });
  }

  loadRegistryStatus(clientId = this.clientService.client?.id): void {
    this.isLoadingRegistryStatus = true;
    this.registryStatusError = null;

    if (!clientId) {
      this.registryStatusError = 'No client selected';
      this.isLoadingRegistryStatus = false;
      return;
    }

    this.registryService.getStatus(clientId).subscribe({
      next: (status) => {
        this.registryStatus = status;
        this.registryStatuses = status.providers ?? [];
        this.isLoadingRegistryStatus = false;
      },
      error: (error) => {
        this.enrichDiagnostics.reportError('enrich.settings.load-registry-status', error);
        this.registryStatusError = 'Failed to load registry cache status';
        this.isLoadingRegistryStatus = false;
      }
    });
  }

  loadHubspotProperties(clientId: string): void {
    this.propertiesError = null;
    this.hubspotPropertiesReady = false;
    this.hubspotProperties = [];
    const requestVersion = ++this.propertiesLoadVersion;

    this.referenceService.getHubspotProperties(clientId).subscribe(
      (properties: HubspotProperty[]) => {
        if (requestVersion !== this.propertiesLoadVersion || this.clientService.client?.id !== clientId) return;
        this.hubspotProperties = properties || [];
        this.hubspotPropertiesReady = true;
      },
      () => {
        if (requestVersion !== this.propertiesLoadVersion || this.clientService.client?.id !== clientId) return;
        this.hubspotProperties = [];
        this.propertiesError = 'Failed to load HubSpot properties for dropdowns.';
      }
    );
  }

  filterHubspotProperties(value: string | null): HubspotProperty[] {
    if (!value) {
      return this.hubspotProperties;
    }
    const filterValue = value.toLowerCase();
    return this.hubspotProperties.filter((prop) => {
      const label = (prop.label || '').toLowerCase();
      const name = (prop.name || '').toLowerCase();
      const groupName = (prop.groupName || '').toLowerCase();
      return label.includes(filterValue) || name.includes(filterValue) || groupName.includes(filterValue);
    });
  }

  displayHubspotProperty = (value: string): string => {
    if (!value) return '';
    const property = this.hubspotProperties.find((p) => p.name === value);
    return property ? property.label : value;
  };

    getPropertyOverwriteLabel(option: PropertyOverwriteOption): string {
        const property = this.hubspotProperties.find((item) => item.name === option.propertyName);
        const label = property?.label || option.label;
        return label === option.propertyName ? label : `${label} (${option.propertyName})`;
    }

  saveSettings(): void {
    this.persistSettings(false);
  }

  onCompanyCaptureChange(enabled: boolean): void {
    this.settingsSaveAttemptVersion++;
    this.companyCaptureAuthorization?.abort();
    this.companyCaptureAccessVersion++;
    this.companyCapturePermissionConnectionId = undefined;
    if (enabled) void this.refreshCompanyCaptureAccess();
    else this.companyCaptureAccess = 'missing';
  }

  async grantCompanyCaptureContactAccess(): Promise<void> {
    const clientId = this.clientService.client?.id;
    const connectionId = this.companyCapturePermissionConnectionId;
    const settingsVersion = this.settingsLoadVersion;
    if (!clientId || !connectionId || this.settingsClientId !== clientId || this.isAuthorizingCompanyCapture) return;
    const authorization = new AbortController();
    this.companyCaptureAuthorization = authorization;
    this.isAuthorizingCompanyCapture = true;
    this.error = null;
    const isCurrent = (): boolean => !this.destroyed && !authorization.signal.aborted &&
      this.clientService.client?.id === clientId && this.settingsLoadVersion === settingsVersion &&
      this.settingsForm.get('companyCaptureEnabled')?.value === true;
    try {
      const authorized = await this.hubspotAuthService.initiateAuth(clientId, connectionId, {
        companyCapture: true, signal: authorization.signal,
      });
      if (!isCurrent()) return;
      if (!authorized || !await this.refreshCompanyCaptureAccess(clientId)) {
        if (isCurrent()) this.error = 'Contact access was not verified. Company capture has not been saved.';
      }
    } finally {
      if (this.companyCaptureAuthorization === authorization) {
        this.companyCaptureAuthorization = undefined;
        this.isAuthorizingCompanyCapture = false;
      }
    }
  }

  private async refreshCompanyCaptureAccess(clientId = this.clientService.client?.id): Promise<boolean> {
    const version = ++this.companyCaptureAccessVersion;
    const settingsVersion = this.settingsLoadVersion;
    const isCurrent = (): boolean => !this.destroyed && !!clientId && this.clientService.client?.id === clientId &&
      this.settingsLoadVersion === settingsVersion && this.companyCaptureAccessVersion === version;
    if (!isCurrent()) return false;
    this.companyCapturePermissionConnectionId = undefined;
    this.companyCaptureAccess = 'checking';
    this.companyCaptureAccessMessage = 'Checking HubSpot contact access…';
    try {
      const connectionId = this.hubspotConnectionId?.trim();
      if (!connectionId) {
        this.companyCaptureAccess = 'unavailable';
        this.companyCaptureAccessMessage = 'Select and save a HubSpot connection in Setup before enabling company capture.';
        return false;
      }
      this.companyCapturePermissionConnectionId = connectionId;
      const granted = await firstValueFrom(this.hubspotAuthService.checkCompanyCaptureAccess(connectionId));
      if (!isCurrent()) return false;
      this.companyCaptureAccess = granted ? 'granted' : 'missing';
      this.companyCaptureAccessMessage = granted
        ? 'HubSpot contact access verified.'
        : 'Grant contact access before saving company capture.';
      return granted;
    } catch {
      if (isCurrent()) {
        this.companyCaptureAccess = 'unavailable';
        this.companyCaptureAccessMessage = 'Contact access could not be verified. Retry before enabling company capture.';
      }
      return false;
    }
  }

  runPropertySetup(): void {
    if (this.settingsForm.invalid) {
      this.error = 'Please fix the form errors before creating properties.';
      return;
    }

    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isSettingUpProperties = true;
    this.error = null;
    this.hubspotService.setup(clientId).subscribe(
      () => {
        this.isSettingUpProperties = false;
        this.setSuccessMessage('HubSpot properties created/updated successfully');
      },
      (error) => {
        this.enrichDiagnostics.reportError('enrich.settings.setup-properties', error);
        this.error = 'Failed to create HubSpot properties';
        this.isSettingUpProperties = false;
      }
    );
  }

  syncNow(): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    if (!this.confirmSyncAllCompanies()) {
      return;
    }

    this.isSyncing = true;
    this.error = null;

    this.hubspotService.syncAllCompanies(clientId).subscribe({
      next: () => {
        this.isSyncing = false;
        this.setSuccessMessage('Sync started successfully. Track progress from sync history.');
      },
      error: (error) => {
        this.enrichDiagnostics.reportError('enrich.settings.start-sync', error);
        this.error = 'Failed to start company sync';
        this.isSyncing = false;
      }
    });
  }

  saveSettingsAndContinueSetup(): void {
    this.persistSettings(true);
  }

  private persistSettings(continueSetup: boolean): void {
    if (this.settingsClientId && this.settingsClientId !== this.clientService.client?.id) {
      this.error = 'Selected client changed. Reload settings before saving.';
      return;
    }
    if (this.settingsForm.invalid) {
      this.error = 'Please fix the form errors before saving';
      return;
    }

    this.isSaving = true;
    this.error = null;
    const saveAttemptVersion = ++this.settingsSaveAttemptVersion;
    const settingsVersion = this.settingsLoadVersion;
    const clientId = this.clientService.client?.id;

    if (!clientId) {
      this.error = 'No client selected';
      this.isSaving = false;
      return;
    }

    const hasSameSelectedClient = (): boolean => this.clientService.client?.id === clientId;
    const isCurrentSaveAttempt = (): boolean =>
      !this.destroyed && saveAttemptVersion === this.settingsSaveAttemptVersion && settingsVersion === this.settingsLoadVersion && hasSameSelectedClient();

    const stopStaleSaveAttempt = (): void => {
      this.error = hasSameSelectedClient()
        ? 'Settings changed before the save completed. Save again.'
        : 'Selected client changed before settings could be saved';
      this.isSaving = false;
    };

    const persist = (dnbReadyForAttempt: boolean, verifiedCaptureConnectionId?: string): void => {
      if (!isCurrentSaveAttempt()) {
        stopStaleSaveAttempt();
        return;
      }

      this.hubspotService.getSettings(clientId).subscribe(
        (currentSettings: EnrichSettings) => {
          if (!isCurrentSaveAttempt()) {
            stopStaleSaveAttempt();
            return;
          }

          const settings = this.buildSettingsPayload(dnbReadyForAttempt);
          if (!settings) {
            this.isSaving = false;
            return;
          }
          if (settings.companyCaptureEnabled === true && (!verifiedCaptureConnectionId ||
            currentSettings.hubspotConnectionId !== verifiedCaptureConnectionId || this.hubspotConnectionId !== verifiedCaptureConnectionId)) {
            this.error = 'The HubSpot connection changed. Reload settings and verify contact access before saving company capture.';
            this.isSaving = false;
            return;
          }
          if (settings.companyCaptureEnabled === true) settings.hubspotConnectionId = verifiedCaptureConnectionId;

          if (continueSetup && currentSettings.setupStatus) {
            settings.setupStatus = {...currentSettings.setupStatus, settingsCreated: true};
          }

          this.hubspotService.updateSettings(settings, clientId).subscribe(
            (saved) => {
              if (!isCurrentSaveAttempt()) {stopStaleSaveAttempt(); return;}
              this.hubspotService.getSettings(clientId).subscribe({
                next: persisted => {
                  if (!isCurrentSaveAttempt()) {stopStaleSaveAttempt(); return;}
                  const expected = saved?.companySyncEligibility ?? settings.companySyncEligibility ?? allCompaniesPolicy();
                  const actual = persisted.companySyncEligibility ?? allCompaniesPolicy();
                  this.settingsForm.get('companySyncEligibility')?.setValue(actual);
                  this.settingsForm.get('hubspotAdditionalProperties')?.setValue(persisted.hubspotAdditionalProperties ?? null, {emitEvent: false});
                  this.isSaving = false;
                  if (settings.companyCaptureEnabled === true && (persisted.companyCaptureEnabled !== true ||
                      persisted.hubspotConnectionId !== verifiedCaptureConnectionId)) {
                    this.error = 'Company capture was submitted, but enabling it could not be verified. Reload before trying again.';
                    return;
                  }
                  if (JSON.stringify(expected) !== JSON.stringify(actual)) {
                    this.error = 'Sync rules changed after saving. Review the current rules before continuing.'; return;
                  }
                  this.setSuccessMessage('Settings saved successfully');
                  if (continueSetup) this.router.navigate(['/enrich/setup']);
                },
                error: () => {if (!isCurrentSaveAttempt()) {stopStaleSaveAttempt(); return;} this.error = 'Settings were submitted, but the saved rules could not be verified. Reload before trying again.'; this.isSaving = false;},
              });
            },
            (error: unknown) => {
              if (!isCurrentSaveAttempt()) { stopStaleSaveAttempt(); return; }
              this.error = this.companyCaptureSaveError(error) || this.additionalPropertiesSaveError(error) || 'Failed to save settings';
              this.isSaving = false;
            }
          );
        },
        () => {
          this.error = 'Failed to load current settings';
          this.isSaving = false;
        }
      );
    };

    const submit = (dnbReadyForAttempt: boolean): void => {
      if (this.settingsForm.get('companyCaptureEnabled')?.value !== true) {
        persist(dnbReadyForAttempt);
        return;
      }
      const connectionId = this.companyCapturePermissionConnectionId || this.hubspotConnectionId;
      if (!connectionId) {
        this.error = 'Verify the HubSpot connection and grant contact access before saving company capture.';
        this.isSaving = false;
        void this.refreshCompanyCaptureAccess(clientId);
        return;
      }
      this.hubspotAuthService.checkCompanyCaptureAccess(connectionId).subscribe({
        next: granted => {
          if (!isCurrentSaveAttempt()) {stopStaleSaveAttempt(); return;}
          if (!granted && this.settingsForm.get('companyCaptureEnabled')?.value === true) {
            this.companyCaptureAccess = 'missing';
            this.error = 'Grant HubSpot contact access before saving company capture.';
            this.isSaving = false;
            return;
          }
          if (granted) {
            this.companyCaptureAccess = 'granted';
            this.companyCaptureAccessMessage = 'HubSpot contact access verified.';
          }
          persist(dnbReadyForAttempt, granted ? connectionId : undefined);
        },
        error: () => {
          if (!isCurrentSaveAttempt()) {stopStaleSaveAttempt(); return;}
          if (this.settingsForm.get('companyCaptureEnabled')?.value !== true) {persist(dnbReadyForAttempt); return;}
          this.error = 'Contact access could not be verified. Company capture has not been saved.';
          this.isSaving = false;
        },
      });
    };

    if (!this.selectedRegistryDataSources.includes('dnb')) {
      submit(false);
      return;
    }

    this.refreshDnbConnectionStatus(clientId, (state) => {
      if (!isCurrentSaveAttempt()) {
        stopStaleSaveAttempt();
        return;
      }
      if (state !== 'ready') {
        this.error = 'Set up the tenant-owned D&B Connection before saving this paid source';
        this.isSaving = false;
        return;
      }
      submit(true);
    });
  }

  reloadSubUnitPreview(): void {
    this.loadSubUnitPreview();
  }

  loadNextSubUnitPreviewPage(): void {
    const after = this.subUnitPreview?.nextAfter;
    if (!after) {
      return;
    }

    this.loadSubUnitPreview(after);
  }

  openSyncHistory(): void {
    this.router.navigate(['/enrich/sync-history']);
  }

  openApiKeys(): void {
    const currentClient = this.clientService.client;
    if (currentClient) {
      this.sidebarService.open(ApiKeysComponent, {client: currentClient});
      return;
    }

    this.toastService.showError('No client selected', 2500);
  }

  copySnippet(snippet: string, event: Event, successMessage?: string): void {
    const button = event.currentTarget as HTMLElement | null;
    const showFeedbackIcon = () => {
      const icon = button?.querySelector('mat-icon') as HTMLElement | null;
      if (!icon) {
        return;
      }
      const previous = icon.innerText;
      icon.innerText = 'check';
      setTimeout(() => (icon.innerText = previous), 1500);
    };

    const notifyCopied = () => {
      showFeedbackIcon();
      const trimmedMessage = successMessage?.trim();
      const message = trimmedMessage && trimmedMessage.length > 0 ? trimmedMessage : 'Copied to clipboard';
      this.toastService.showSuccess(message, 2500);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(snippet)
        .then(notifyCopied)
        .catch(() => {
          this.copyFallback(snippet);
          notifyCopied();
        });
    } else {
      this.copyFallback(snippet);
      notifyCopied();
    }
  }

  private buildSettingsPayload(
    dnbReadyForAttempt: boolean,
  ): Partial<EnrichSettings> | null {
    const formValues = this.settingsForm.getRawValue();
    const enabledProviders = normalizeSettingsProviders({dataProviders: formValues.dataProviders});
    const enabledDataSources = normalizeSettingsDataSources({dataSources: formValues.dataSources});

    if (enabledProviders.length === 0) {
      this.error = 'Select at least one registry provider before saving';
      return null;
    }

    if (
      enabledDataSources.includes('dnb') &&
      !dnbReadyForAttempt
    ) {
      this.error = 'Set up the tenant-owned D&B Connection before enabling this paid source';
      return null;
    }

    const defaultProvider: RegistryProvider = enabledProviders.includes('brreg') ? 'brreg' : enabledProviders[0];

    return {
      ...(formValues.hubspotAdditionalProperties === null ? {} : {hubspotAdditionalProperties: formValues.hubspotAdditionalProperties}),
      companySyncEligibility: formValues.companySyncEligibility,
      subscriptionActive: formValues.subscriptionActive,
      syncOnCompanyCreated: formValues.syncOnCompanyCreated,
      existingCompanySyncFrequency: formValues.existingCompanySyncFrequency,
      addCompaniesMatchingFilters: formValues.addCompaniesMatchingFilters,
      companyCaptureEnabled: !!formValues.companyCaptureEnabled,
      dataProvider: defaultProvider,
      dataProviders: enabledProviders,
      dataSources: enabledDataSources,
      dnbCountryISOAlpha2Code: formValues.dnbCountryISOAlpha2Code || null,
      announcementsTimelineSyncEnabled: enabledProviders.includes('brreg') && !!formValues.announcementsTimelineSyncEnabled,
        propertyOverwriteToggles: this.buildPropertyOverwritePayload(formValues.propertyOverwriteToggles),
      crmOrgNumberProperty: formValues.crmOrgNumberProperty,
      crmSubUnitNumberProperty: formValues.crmSubUnitNumberProperty,
      populateParentOrganizationNumberOnSubUnits:
        !!formValues.populateParentOrganizationNumberOnSubUnits,
      lastUpdateTimestamp: new Date().toISOString()
    };
  }

  get additionalPropertyIdentityNames(): string[] {
    return [this.settingsForm.get('crmOrgNumberProperty')?.value, this.settingsForm.get('crmSubUnitNumberProperty')?.value]
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
  }

  private companyCaptureSaveError(error: unknown): string | null {
    const code = (error as {error?: {error?: string}})?.error?.error;
    const messages: Record<string, string> = {
      company_capture_contact_access_required: 'Grant HubSpot contact access before saving company capture.',
      company_capture_connection_changed: 'The HubSpot connection changed. Reload settings and verify contact access before saving company capture.',
      company_capture_permissions_unavailable: 'Contact access could not be verified. Retry before saving company capture.',
    };
    if (!code || !Object.prototype.hasOwnProperty.call(messages, code)) return null;
    this.companyCaptureAccessVersion++;
    this.companyCaptureAccess = code === 'company_capture_contact_access_required' ? 'missing' : 'unavailable';
    this.companyCaptureAccessMessage = messages[code];
    if (code === 'company_capture_connection_changed') this.companyCapturePermissionConnectionId = undefined;
    return messages[code];
  }

  private additionalPropertiesSaveError(error: unknown): string | null {
    if (!isRecord(error) || !isRecord(error['error']) || error['error']['error'] !== 'invalid_additional_company_properties') return null;
    const issues = error['error']['issues'];
    const issue = Array.isArray(issues) && isRecord(issues[0]) ? issues[0] : null;
    const messages: Record<string, string> = {
      connection_changed_revalidate: 'The HubSpot connection changed. Reload settings and review the additional properties.',
      portal_verification_unavailable: 'The HubSpot portal could not be verified. Retry before saving additional properties.',
      property_metadata_unavailable: 'HubSpot Company properties could not be loaded. Retry before saving.',
      property_outside_connection_scope: 'The property is outside the allowed scope for this connection.',
      ambiguous_property_scope: 'Select an unambiguous HubSpot connection before configuring additional properties.',
      protected_property: 'The property is managed by Shine and cannot be used as a fixed fallback.',
      property_not_writable: 'The Company property is read-only or otherwise unsuitable for a shared fallback.',
      unknown_company_property: 'The Company property does not exist in this portal.',
      invalid_option_value: 'Select an internal option value for the Company property.',
    };
    const code = issue && typeof issue['code'] === 'string' ? issue['code'] : '';
    const action = issue && (issue['action'] === 'create' || issue['action'] === 'update') ? issue['action'] : null;
    const row = issue && typeof issue['index'] === 'number' ? issue['index'] + 1 : null;
    return `${action && row ? `${action === 'create' ? 'Create' : 'Update'} company, row ${row}: ` : ''}${messages[code] || 'Review the additional company property configuration.'}`;
  }

  private ensureSubUnitPreviewLoaded(): void {
    if (!this.canPreviewSubUnits) {
      this.resetSubUnitPreviewState();
      return;
    }

    if (this.hasLoadedSubUnitPreview || this.isLoadingSubUnitPreview) {
      return;
    }

    this.loadSubUnitPreview();
  }

  private resetSubUnitPreviewState(): void {
    this.hasLoadedSubUnitPreview = false;
    this.subUnitPreview = null;
    this.subUnitPreviewError = null;
  }

  private loadSubUnitPreview(after?: string): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.subUnitPreviewError = 'No client selected';
      return;
    }

    if (!this.canPreviewSubUnits) {
      this.resetSubUnitPreviewState();
      return;
    }

    this.isLoadingSubUnitPreview = true;
    this.subUnitPreviewError = null;

    this.hubspotService.getSubUnitPreview(clientId, {
      limit: this.subUnitPreviewPageSize,
      after,
    }).subscribe({
      next: (preview) => {
        this.subUnitPreview = preview;
        this.hasLoadedSubUnitPreview = true;
        this.isLoadingSubUnitPreview = false;
      },
      error: (error) => {
        this.enrichDiagnostics.reportError('enrich.settings.load-subunit-preview', error);
        this.subUnitPreviewError = error?.error?.error || 'Failed to load BRREG sub-unit preview';
        this.isLoadingSubUnitPreview = false;
      }
    });
  }

  previewRegistryStatusLabel(row: HubspotSubUnitPreviewRow): string {
    if (row.registryStatus === 'matched') return 'Matched';
    if (row.registryStatus === 'not_found') return 'Not found';
    return 'Error';
  }

  previewIssueLabel(issueCode: 'duplicate' | 'conflict'): string {
    return issueCode === 'duplicate' ? 'Duplicate' : 'Conflict';
  }

  previewPropertyList(propertyNames: string[]): string {
    return propertyNames.join(', ');
  }

  private confirmSyncAllCompanies(): boolean {
    return window.confirm(
      `Start full company sync?\n\n${this.syncAllImpactText}\n\nUse Sync history to monitor progress and results.`
    );
  }

    private buildPropertyOverwritePayload(value: unknown): Record<string, boolean> {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return {};
        }

        return Object.entries(value as Record<string, unknown>).reduce((toggles, [propertyName, enabled]) => {
            const key = propertyName.trim();
            if (key && enabled === true) {
                toggles[key] = true;
            }
            return toggles;
        }, {} as Record<string, boolean>);
    }

    private syncPropertyOverwriteControls(settings: EnrichSettings): void {
        const controls = this.propertyOverwriteToggleGroup;
        const options = this.buildPropertyOverwriteOptions(settings);
        const optionNames = new Set(options.map((option) => option.propertyName));

        Object.keys(controls.controls).forEach((controlName) => {
            if (!optionNames.has(controlName)) {
                controls.removeControl(controlName);
            }
        });

        options.forEach((option) => {
            const enabled = settings.propertyOverwriteToggles?.[option.propertyName] === true;
            if (controls.contains(option.propertyName)) {
                controls.get(option.propertyName)?.setValue(enabled, {emitEvent: false});
            } else {
                controls.addControl(option.propertyName, new FormControl(enabled, {nonNullable: true}));
            }
        });

        this.propertyOverwriteOptions = options;
    }

    private buildPropertyOverwriteOptions(settings: EnrichSettings): PropertyOverwriteOption[] {
        const options = new Map<string, PropertyOverwriteOption>();
        const addOption = (option: PropertyOverwriteOption): void => {
            const propertyName = option.propertyName.trim();
            if (!propertyName || !this.isConfigurableOverwriteProperty(propertyName, settings) || options.has(propertyName)) {
                return;
            }

            options.set(propertyName, {...option, propertyName});
        };

        STANDARD_PROPERTY_OVERWRITE_OPTIONS.forEach(addOption);
        (settings.mapping ?? []).forEach((mapping) => {
            const source = String(mapping?.sourceField ?? '').trim();
            const target = String(mapping?.targetField ?? '').trim();
            const enabled = mapping?.enabled !== false;
            if (!target || !enabled) {
                return;
            }

            addOption({
                propertyName: target,
                label: this.formatPropertyName(target),
                source: source || 'Custom mapping',
            });
        });

        Object.keys(settings.propertyOverwriteToggles ?? {}).forEach((propertyName) => {
            addOption({
                propertyName,
                label: this.formatPropertyName(propertyName),
                source: 'Existing setting',
            });
        });

        return Array.from(options.values());
    }

    private isConfigurableOverwriteProperty(propertyName: string, settings: EnrichSettings): boolean {
        if (propertyName.startsWith('shine_enrich_') || ALWAYS_OVERWRITE_PROPERTIES.has(propertyName)) {
            return false;
        }

        return ![settings.crmOrgNumberProperty, settings.crmSubUnitNumberProperty]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .includes(propertyName);
    }

    private formatPropertyName(propertyName: string): string {
        return propertyName
            .split('_')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

  private copyFallback(snippet: string): void {
    const textarea = this.renderer.createElement('textarea');
    textarea.value = snippet;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    this.renderer.appendChild(document.body, textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      this.renderer.removeChild(document.body, textarea);
    }
  }

  private buildCompanyCaptureSnippets(portalId: number): void {
    const baseUrl = environment.enrichBaseServerUrl;
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const clientId = this.clientService.client?.id ?? '<clientId>';
    const portalIdValue = portalId;

    this.companyCaptureScriptSnippet = `<script>
  window.shineEnrichCompanyCaptureSettings = {
    baseUrl: '${normalizedBaseUrl}',
    portalId: ${portalIdValue},
    formSelector: '#hs_form_target_widget_1768316166464 form',
    companyFieldName: 'company',
    orgNumberFieldName: 'organisasjonsnummer',
    requireCompanySelection: false,
    selectionRequiredMessage: 'Please select a company from the list.',
    limit: 10
  };
</script>
<script async src="${normalizedBaseUrl}/company-capture/v1.js"></script>`;
/* obsolete inline asset retained here temporarily */
/*
  (function () {
    function debounce(fn, ms) {
      let t;
      return function () {
        const args = arguments;
        clearTimeout(t);
        t = setTimeout(function () {
          fn.apply(null, args);
        }, ms);
      };
    }

    function renderList(container, items, onPick) {
      container.innerHTML = '';
      if (!items.length) return;

      const ul = document.createElement('ul');
      ul.style.margin = '6px 0 0';
      ul.style.padding = '0';
      ul.style.listStyle = 'none';
      ul.style.border = '1px solid #ddd';
      ul.style.borderRadius = '6px';
      ul.style.background = '#fff';
      ul.style.maxHeight = '240px';
      ul.style.overflow = 'auto';
      ul.style.position = 'absolute';
      ul.style.left = '0';
      ul.style.right = '0';
      ul.style.zIndex = '9999';

      items.forEach(function (item) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.style.width = '100%';
        btn.style.textAlign = 'left';
        btn.style.padding = '10px 12px';
        btn.style.border = '0';
        btn.style.background = 'transparent';
        btn.style.cursor = 'pointer';
        btn.onmouseenter = function () {
          btn.style.background = '#f6f6f6';
        };
        btn.onmouseleave = function () {
          btn.style.background = 'transparent';
        };

        const location = [item.postalCode, item.city].filter(Boolean).join(' ');
        btn.textContent = item.name + ' (' + item.orgNumber + ')' + (location ? ' — ' + location : '');

        btn.onclick = function () {
          onPick(item);
          container.innerHTML = '';
        };

        li.appendChild(btn);
        ul.appendChild(li);
      });

      container.appendChild(ul);
    }

    function waitFor(getter, cb, opts) {
      opts = opts || {};
      const intervalMs = opts.intervalMs || 100;
      const timeoutMs = opts.timeoutMs || 10000;
      const start = Date.now();

      (function tick() {
        try {
          const val = getter();
          if (val) return cb(val);
        } catch (_) {
        }

        if (Date.now() - start >= timeoutMs) return;
        setTimeout(tick, intervalMs);
      })();
    }

    function initShineEnrichCompanyCapture(opts) {
      const baseUrl = (opts && opts.baseUrl) || 'https://api.shine-enrich.intuvio.com';
      const cleanBase = baseUrl.replace(new RegExp('/$'), '');
      const portalId = Number(opts && opts.portalId);
      const formSelector = (opts && opts.formSelector) || 'form';
      const companyFieldName = (opts && opts.companyFieldName) || 'company';
      const orgNumberFieldName = (opts && opts.orgNumberFieldName) || 'orgnr';
      const selectionRequiredMessage =
              String((opts && opts.selectionRequiredMessage) || 'Please select a company from the list.').trim() ||
              'Please select a company from the list.';
      const limit = Math.min(Math.max(Number((opts && opts.limit) || 10) || 10, 1), 20);

      if (!portalId) return;

      waitFor(
              function () {
                const form = document.querySelector(formSelector);
                if (!form) return null;

                const companyInput = form.querySelector('input[name="' + companyFieldName + '"]');
                const orgInput = form.querySelector('input[name="' + orgNumberFieldName + '"]');
                if (!companyInput || !orgInput) return null;

                return {form: form, companyInput: companyInput, orgInput: orgInput};
              },
              function (ctx) {
                const companyInput = ctx.companyInput;
                const orgInput = ctx.orgInput;

                if (companyInput.dataset.shineEnrichBound === '1') return;
                companyInput.dataset.shineEnrichBound = '1';

                const listContainer = document.createElement('div');
                listContainer.dataset.shineEnrichCompanySuggestions = '1';
                listContainer.style.position = 'relative';
                listContainer.style.display = 'block';
                listContainer.style.width = '100%';
                companyInput.parentNode.style.position = companyInput.parentNode.style.position || 'relative';
                companyInput.parentNode.appendChild(listContainer);

                const fieldContainer = companyInput.closest('.hs-form-field') || companyInput.parentNode;
                let selectedValue = String(orgInput.value || '').trim() ? String(companyInput.value || '') : '';
                let canSuggest = !selectedValue;
                let selectionError = null;

                function clearSelectionError() {
                  companyInput.setCustomValidity('');
                  if (selectionError) {
                    selectionError.remove();
                    selectionError = null;
                  }
                }

                function showSelectionError() {
                  const companyValue = String(companyInput.value || '').trim();
                  const orgNumberValue = String(orgInput.value || '').trim();
                  if (!companyValue || orgNumberValue) {
                    clearSelectionError();
                    return false;
                  }

                  companyInput.setCustomValidity(selectionRequiredMessage);
                  if (!selectionError) {
                    selectionError = document.createElement('ul');
                    selectionError.dataset.shineEnrichCompanySelectionError = '1';
                    selectionError.className = 'no-list hs-error-msgs inputs-list';
                    selectionError.setAttribute('role', 'alert');

                    const item = document.createElement('li');
                    const label = document.createElement('label');
                    label.className = 'hs-error-msg hs-main-font-element';
                    label.textContent = selectionRequiredMessage;
                    item.appendChild(label);
                    selectionError.appendChild(item);
                    fieldContainer.appendChild(selectionError);
                  }
                  return true;
                }

                function closeList() {
                  listContainer.innerHTML = '';
                }

                function dismissList() {
                  const hadSuggestions = listContainer.hasChildNodes();
                  closeList();
                  if (hadSuggestions) showSelectionError();
                }

                const fetchSuggestions = debounce(function () {
                  if (!canSuggest) {
                    closeList();
                    return;
                  }

                  const q = String(companyInput.value || '').trim();
                  if (q.length < 2) {
                    closeList();
                    return;
                  }

                  const url =
                          cleanBase +
                          '/api/company-capture/suggest?portalId=' +
                          encodeURIComponent(String(portalId)) +
                          '&q=' +
                          encodeURIComponent(q) +
                          '&limit=' +
                          encodeURIComponent(String(limit));

                  fetch(url)
                          .then(function (r) {
                            return r.ok ? r.json() : Promise.resolve([]);
                          })
                          .then(function (items) {
                            renderList(listContainer, Array.isArray(items) ? items : [], function (picked) {
                              companyInput.value = picked.name || '';
                              orgInput.value = picked.orgNumber || '';

                              selectedValue = companyInput.value;
                              canSuggest = false;
                              clearSelectionError();

                              companyInput.dispatchEvent(new Event('input', {bubbles: true}));
                              orgInput.dispatchEvent(new Event('input', {bubbles: true}));
                              orgInput.dispatchEvent(new Event('change', {bubbles: true}));

                              closeList();
                            });
                          })
                          .catch(function () {
                          });
                }, 200);

                companyInput.addEventListener('input', function () {
                  const v = String(companyInput.value || '');
                  clearSelectionError();
                  if (!canSuggest && selectedValue && v !== selectedValue) {
                    canSuggest = true;
                    selectedValue = '';
                    orgInput.value = '';
                    orgInput.dispatchEvent(new Event('input', {bubbles: true}));
                    orgInput.dispatchEvent(new Event('change', {bubbles: true}));
                  }
                  fetchSuggestions();
                });

                companyInput.addEventListener('focus', function () {
                  if (!canSuggest) closeList();
                });
                companyInput.addEventListener('click', function () {
                  if (!canSuggest) closeList();
                });
                companyInput.addEventListener('keydown', function (e) {
                  if (e.key === 'Escape') dismissList();
                });
                companyInput.addEventListener('blur', function () {
                  setTimeout(function () {
                    if (listContainer.contains(document.activeElement)) return;
                    dismissList();
                  }, 0);
                });

                document.addEventListener('click', function (e) {
                  if (e.target === companyInput || listContainer.contains(e.target)) return;
                  dismissList();
                });

                ctx.form.addEventListener('submit', function (e) {
                  if (!showSelectionError()) return;
                  e.preventDefault();
                  companyInput.reportValidity();
                }, true);
              },
              {intervalMs: 100, timeoutMs: 15000}
      );
    }

    initShineEnrichCompanyCapture({
      baseUrl: 'https://api.shine-enrich.intuvio.com',
      portalId: ${portalIdValue},
      formSelector: '#hs_form_target_widget_1768316166464 form',
      companyFieldName: 'company',
      orgNumberFieldName: 'organisasjonsnummer',
      selectionRequiredMessage: 'Please select a company from the list.',
      limit: 10
    });
  })();
</script>`;
*/

    this.companyCaptureWorkflowSnippet = `curl -X POST '${normalizedBaseUrl}/api/company-capture/process?clientId=${encodeURIComponent(clientId)}' \\
  -H 'x-api-key: <YOUR_SHINE_API_KEY>' \\
  -H 'content-type: application/json' \\
  -d '{"orgNumber":"{{contact.orgnr}}","email":"{{contact.email}}"}'`;
  }

  private syncAnnouncementsToggleWithProviders(providers: RegistryProvider[]): void {
    const announcementsControl = this.settingsForm.get('announcementsTimelineSyncEnabled');
    if (!announcementsControl) {
      return;
    }

    const brregEnabled = providers.includes('brreg');
    if (!brregEnabled && announcementsControl.value) {
      announcementsControl.setValue(false);
    }

    if (brregEnabled) {
      announcementsControl.enable({emitEvent: false});
      return;
    }

    announcementsControl.disable({emitEvent: false});
  }

  private setSuccessMessage(message: string): void {
    this.toastService.showSuccess(message, 4000);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.companyCaptureAuthorization?.abort();
    this.companyCaptureAccessVersion++;
  }
}
